// ===============================
// pages/Cart.jsx
// ===============================

import React, { useContext, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "../../context/CartContext";
import "./Cart.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
const FALLBACK_IMAGE = "https://via.placeholder.com/300x300?text=No+Image";

const Cart = () => {
  const navigate = useNavigate();

  const {
    cartItems,
    removeFromCart,
    increaseQty,
    decreaseQty,
    totalPrice,
    shippingFee,
    couponCode,
    setCouponCode,
    discount,
    couponMessage,
    couponApplied,
    couponLoading,
    finalTotal,
    applyCoupon,
    removeCoupon,
  } = useContext(CartContext);

  const totalItems = useMemo(() => {
    return cartItems.reduce(
      (acc, item) => acc + Number(item.quantity || 1),
      0
    );
  }, [cartItems]);

  const resolveImageUrl = (img) => {
    if (!img || String(img).trim() === "") return FALLBACK_IMAGE;
    if (img.startsWith("http://") || img.startsWith("https://")) return img;
    
    let cleanPath = img.replace(/^\/?uploads\//, "");
    const baseUrl = API_BASE_URL.endsWith("/") ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
    return `${baseUrl}/uploads/${cleanPath}`;
  };

  const getCartItemImage = (item) => {
    if (item?.image && String(item.image).trim() !== "") {
      return resolveImageUrl(item.image);
    }
    if (Array.isArray(item?.images) && item.images.length > 0 && item.images[0]) {
      return resolveImageUrl(item.images[0]);
    }
    return FALLBACK_IMAGE;
  };

  // 🎯 FIX: Pass the whole item and check for nested product.category
  const getCategoryName = (item) => {
    const cat = item?.category || item?.product?.category;
    
    if (!cat) return "Uncategorized";
    if (typeof cat === "object") return cat.name || "Uncategorized";
    return String(cat);
  };

  return (
    <div className="cart-page">
      <div className="cart-header">
        <div className="container">
          <span className="cart-badge">Shopping Cart</span>
          <h1>Your Cart, Ready For Checkout</h1>
          <p>
            Review your products, update quantities, and apply coupons before
            placing your order.
          </p>
        </div>
      </div>

      {cartItems.length === 0 ? (
        <div className="container">
          <div className="empty-cart">
            <div className="empty-cart-icon">
              <i className="fa-solid fa-cart-shopping"></i>
            </div>
            <h2>Your Cart Is Empty</h2>
            <p>
              Looks like you haven’t added anything yet. Start exploring products
              and add your favorites to cart.
            </p>
            <Link to="/shop" className="theme-btn">
              Continue Shopping
            </Link>
          </div>
        </div>
      ) : (
        <div className="container cart-wrapper">
          {/* Cart Items List */}
          <div className="cart-items">
            {cartItems.map((item) => (
              <div className="cart-card" key={item._id || item.id}>
                
                {/* 1. Left Thumbnail Image */}
                <div className="cart-image">
                  <img
                    src={getCartItemImage(item)}
                    alt={item.name || "Product"}
                    onError={(e) => {
                      e.currentTarget.src = FALLBACK_IMAGE;
                    }}
                  />
                </div>

                {/* 2. Right Info Block */}
                <div className="cart-info">
                  <div className="cart-info-top">
                    {/* 🎯 FIX: Apply the updated function here */}
                    <span className="cart-category">
                      {getCategoryName(item)}
                    </span>
                    <button
                      type="button"
                      className="remove-btn-icon"
                      onClick={() => removeFromCart(item._id || item.id)}
                      title="Remove Item"
                    >
                      <i className="fa-regular fa-trash-can"></i>
                    </button>
                  </div>
                  
                  <h3>{item.name}</h3>
                  
                  <p className="cart-price">
                    ₹{Number(item.price || 0)}
                  </p>

                  <div className="cart-action-row">
                    <div className="qty-box">
                      <button
                        type="button"
                        aria-label="Decrease quantity"
                        onClick={() => decreaseQty(item._id || item.id)}
                      >
                        -
                      </button>
                      <span>{Number(item.quantity || 1)}</span>
                      <button
                        type="button"
                        aria-label="Increase quantity"
                        onClick={() => increaseQty(item._id || item.id)}
                      >
                        +
                      </button>
                    </div>

                    <h4 className="item-total">
                      ₹{Number(item.price || 0) * Number(item.quantity || 1)}
                    </h4>
                  </div>
                </div>

              </div>
            ))}
          </div>

          {/* Right Side Order Summary */}
          <div className="cart-summary">
            <h2>Order Summary</h2>

            <div className="summary-row">
              <span>Total Items</span>
              <span>{totalItems}</span>
            </div>

            <div className="summary-row">
              <span>Subtotal</span>
              <span>₹{totalPrice}</span>
            </div>

            {/* Dynamic Shipping Row */}
            <div className="summary-row">
              <span>Shipping</span>
              <span>
                {shippingFee === 0 ? (
                  <strong style={{ color: "#2ecc71" }}>Free</strong>
                ) : (
                  `₹${shippingFee}`
                )}
              </span>
            </div>

            {/* Free Shipping Incentive Message */}
            {totalPrice > 0 && totalPrice < 499 && (
              <p style={{ fontSize: "0.82rem", color: "#e67e22", margin: "4px 0 10px" }}>
                Add <strong>₹{499 - totalPrice}</strong> more for <strong>Free Shipping!</strong>
              </p>
            )}

            <div className="coupon-section">
              <h4>Have a Coupon?</h4>
              <div className="coupon-box">
                <input
                  type="text"
                  placeholder="Enter Code"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  disabled={couponApplied || couponLoading}
                />
                <button
                  type="button"
                  onClick={applyCoupon}
                  disabled={couponLoading || couponApplied || !couponCode.trim()}
                >
                  {couponLoading ? "..." : couponApplied ? "Applied" : "Apply"}
                </button>
              </div>

              {couponApplied && (
                <button
                  type="button"
                  className="remove-coupon-btn"
                  onClick={removeCoupon}
                >
                  Remove Coupon
                </button>
              )}

              {couponMessage && (
                <p className={`coupon-message ${couponApplied ? "success" : "error"}`}>
                  <i className={`fa-solid ${couponApplied ? 'fa-circle-check' : 'fa-circle-exclamation'}`}></i> {couponMessage}
                </p>
              )}
            </div>

            {discount > 0 && (
              <div className="summary-row discount">
                <span>Coupon Discount</span>
                <span>- ₹{discount}</span>
              </div>
            )}

            <hr className="summary-divider" />

            <div className="summary-row total">
              <span>Grand Total</span>
              <span>₹{finalTotal}</span>
            </div>

            <button
              className="checkout-btn"
              onClick={() => navigate("/checkout")}
            >
              Proceed To Checkout
            </button>

            <Link to="/shop" className="continue-shopping">
              <i className="fa-solid fa-arrow-left"></i> Continue Shopping
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;